import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Barra, Carregando } from '@/componentes/Esqueleto';
import { RodapeDeContagem } from '@/componentes/Tabela';
import { formatarInteiro, formatarReais } from '@/lib/formato';

import { custoParadoEscrito, linhaDaMaquinaDoGrupo, type GrupoDaFila } from './gestor';

/**
 * A FILA AGRUPADA DA R06 (p.72): Obra (ou Centro de custo) · Máquinas ·
 * Pontos · Custo parado (R$/dia). Cada grupo abre as máquinas que o puxam,
 * como subitens, da maior pontuação para a menor; o primeiro grupo vem aberto.
 *
 * O grupo soma os pontos das máquinas na fila (nenhum ponto novo) e o custo
 * parado delas — a premissa do custo parado vem do servidor e fica escrita
 * abaixo da fila, com as outras regras.
 *
 * É uma tabela própria, e não a `Tabela` do sistema, porque a linha tem
 * subitens; o resto segue a p.27: cabeçalho de 40 px no rebaixado, linhas de
 * 48, número à direita com `tabular-nums`, rodapé com a contagem.
 */
export function FilaAgrupada({
  eixo,
  grupos,
  carregando,
  vazio,
}: {
  readonly eixo: 'obra' | 'centro';
  readonly grupos: readonly GrupoDaFila[];
  readonly carregando: boolean;
  readonly vazio: ReactNode;
}) {
  const [abertos, setAbertos] = useState<ReadonlySet<string> | null>(null);
  const chave = (g: GrupoDaFila) => g.id ?? 'sem-grupo';
  /* Sem escolha da pessoa ainda, o primeiro grupo — o que mais pede — vem aberto. */
  const aberto = (g: GrupoDaFila, i: number) => (abertos === null ? i === 0 : abertos.has(chave(g)));
  const alternar = (g: GrupoDaFila, i: number) => {
    const atual = new Set(abertos ?? (grupos[0] ? [chave(grupos[0])] : []));
    if (aberto(g, i)) atual.delete(chave(g));
    else atual.add(chave(g));
    setAbertos(atual);
  };
  const rotuloDoGrupo = eixo === 'obra' ? 'Obra' : 'Centro de custo';

  if (carregando) {
    return (
      <div className="cartao overflow-hidden">
        <Carregando oQue="Carregando a fila agrupada" className="flex flex-col">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-[var(--linha-tabela)] items-center gap-4 border-b border-borda-sutil px-4">
              <Barra className="h-3 w-1/3" />
              <Barra className="ml-auto h-3 w-16" />
              <Barra className="h-3 w-20" />
            </div>
          ))}
        </Carregando>
      </div>
    );
  }
  if (grupos.length === 0) return <div className="cartao p-6">{vazio}</div>;

  return (
    <div className="cartao overflow-x-auto">
      <table className="w-full border-collapse" aria-label={eixo === 'obra' ? 'Fila de prioridades por obra' : 'Fila de prioridades por centro de custo'}>
        <thead>
          <tr className="h-[var(--tabela-cabecalho)] bg-rebaixada txt-legenda text-tinta-secundaria">
            <th scope="col" className="w-10" />
            <th scope="col" className="px-4 text-left font-medium">{rotuloDoGrupo}</th>
            <th scope="col" className="px-4 text-left font-medium">Máquinas</th>
            <th scope="col" className="px-4 text-right font-medium">Pontos</th>
            <th scope="col" className="px-4 text-right font-medium">Custo parado</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((g, i) => {
            const estaAberto = aberto(g, i);
            return [
              <tr key={chave(g)} className={`h-[var(--linha-tabela)] border-t border-borda-sutil ${estaAberto ? 'bg-rebaixada' : ''}`}>
                <td className="pl-2">
                  <button
                    type="button"
                    className="botao-fechar"
                    aria-expanded={estaAberto}
                    aria-label={`${estaAberto ? 'Fechar' : 'Abrir'} as máquinas de ${g.nome}`}
                    onClick={() => {
                      alternar(g, i);
                    }}
                  >
                    {estaAberto ? (
                      <ChevronDown aria-hidden="true" className="size-[var(--icone-controle)]" />
                    ) : (
                      <ChevronRight aria-hidden="true" className="size-[var(--icone-controle)]" />
                    )}
                  </button>
                </td>
                <td className="px-4">
                  {/* Centro de custo com o código antes do nome (p.72). */}
                  <span className={`txt-corpo font-semibold ${g.id === null ? 'text-tinta-secundaria' : 'text-tinta'}`}>
                    {eixo === 'centro' && g.codigo ? <span className="codigo">{`${g.codigo} · `}</span> : null}
                    {g.nome}
                  </span>
                </td>
                <td className="px-4 txt-corpo text-tinta-secundaria">
                  {`${formatarInteiro(g.maquinas.length)} ${g.maquinas.length === 1 ? 'máquina' : 'máquinas'}`}
                </td>
                <td className="px-4 text-right">
                  <span className="txt-corpo font-semibold tabular-nums text-tinta">{formatarInteiro(g.pontos)}</span>
                  <span className="txt-corpo text-tinta-apagada"> pts</span>
                </td>
                <td
                  className="px-4 text-right txt-corpo tabular-nums text-tinta"
                  title={
                    g.maquinasSemCustoPorHora
                      ? `${formatarInteiro(g.maquinasSemCustoPorHora)} ${g.maquinasSemCustoPorHora === 1 ? 'máquina' : 'máquinas'} sem custo por hora, fora da soma`
                      : undefined
                  }
                >
                  {custoParadoEscrito(g.custoParadoPorDia)}
                </td>
              </tr>,
              ...(estaAberto
                ? g.maquinas.map((m) => (
                    <tr key={`${chave(g)}-${m.equipamentoId}`} className="h-[var(--linha-tabela)] border-t border-borda-sutil">
                      <td />
                      <td colSpan={2} className="py-2 pr-4 pl-8">
                        <span className="txt-apoio text-tinta-secundaria">
                          <span className="codigo text-tinta">{m.codigo}</span>
                          {linhaDaMaquinaDoGrupo(m) ? ` · ${linhaDaMaquinaDoGrupo(m)}` : ''}
                        </span>
                      </td>
                      <td className="px-4 text-right">
                        <span className="txt-apoio font-semibold tabular-nums text-tinta">{formatarInteiro(m.pontos)}</span>
                        <span className="txt-apoio text-tinta-apagada"> pts</span>
                      </td>
                      <td className="px-4 text-right txt-apoio tabular-nums text-tinta-secundaria" title={m.custoParadoPorDia ? formatarReais(m.custoParadoPorDia) : undefined}>
                        {m.custoParadoPorDia ? custoParadoEscrito(m.custoParadoPorDia) : ''}
                      </td>
                    </tr>
                  ))
                : []),
            ];
          })}
        </tbody>
      </table>
      <div className="border-t border-borda-sutil">
        <RodapeDeContagem total={grupos.length} />
      </div>
    </div>
  );
}
